import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCallIntelligencePLGV1Service, serviceName } from '../service';
import { GetUserOnboardingStatusIO, ServiceQueries } from '../types';
import { useSchemaCallIntelligencePLGV1Queries } from './use-call-intelligence-v1-queries';
import {
  UseSchemaCallIntelligencePLGV1QueryEndpointArgs,
  useSchemaCallIntelligencePLGV1Query,
} from './use-call-intelligence-v1-query';

/**
 * A hook that returns a query for the `GetUserOnboardingStatus` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetUserOnboardingStatusQuery = <E = unknown, D = GetUserOnboardingStatusIO['output']>(
  args: UseSchemaCallIntelligencePLGV1QueryEndpointArgs<'GetUserOnboardingStatus', E, D>
) =>
  useSchemaCallIntelligencePLGV1Query({
    endpointName: 'GetUserOnboardingStatus',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetUserOnboardingStatus` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetUserOnboardingStatusQueries = <E = unknown, D = GetUserOnboardingStatusIO['output']>(
  argsArr: UseSchemaCallIntelligencePLGV1QueryEndpointArgs<'GetUserOnboardingStatus', E, D>[]
) =>
  useSchemaCallIntelligencePLGV1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetUserOnboardingStatus',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetUserOnboardingStatus` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetUserOnboardingStatus = () => {
  const queryClient = useQueryClient();

  const fetchGetUserOnboardingStatus = async <E = unknown, D = GetUserOnboardingStatusIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSchemaCallIntelligencePLGV1QueryEndpointArgs<'GetUserOnboardingStatus', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetUserOnboardingStatusIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetUserOnboardingStatus'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetUserOnboardingStatusIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetUserOnboardingStatus'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetUserOnboardingStatus'>({
          serviceName,
          endpointName: 'GetUserOnboardingStatus',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCallIntelligencePLGV1Service.GetUserOnboardingStatus(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetUserOnboardingStatus;
};
